import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Info } from 'lucide-react';
import mixpanel from 'mixpanel-browser';
import MarkdownTooltip from '@/components/ui/MarkdownTooltip';
import DataRequestInput from '@/components/ui/DataRequestInput';
import { API_URL } from '@/lib/config';

mixpanel.init('69f901b85ec802f6e0a1ddfae6b916e5', { debug: true });

const Switch = ({ checked, onChange, label }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer">
    <div className="relative">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onChange}
      />
      <div className={`block w-10 h-6 rounded-full transition-colors duration-200 ease-in-out ${checked ? 'bg-[#FE8002]' : 'bg-gray-300'}`} />
      <div
        className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
          checked ? 'transform translate-x-4' : 'transform translate-x-0'
        }`}
      />
    </div>
    <span className="text-sm text-gray-600">{label}</span>
  </label>
);

const DisabledDatePicker = ({ onClose, type, position }) => {
  // Calculate if the picker would go off-screen
  const pickerWidth = 320; // width of the picker in pixels
  const viewportWidth = window.innerWidth;
  
  // Adjust x position if it would overflow the viewport
  const adjustedX = Math.min(position.x, viewportWidth - pickerWidth - 16); // 16px padding from edge
  
  return (
    <>
      <div 
        className="fixed inset-0 z-40"
        onClick={onClose}
      />
      <div 
        className="fixed z-50 bg-white rounded-lg p-4 shadow-xl w-[320px]"
        style={{
          top: `${position.y}px`,
          left: `${adjustedX}px`,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold">Select Date Range</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        
        <div className="space-y-3">
          <div className="bg-gray-100 rounded-lg p-3 opacity-50 cursor-not-allowed">
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-md p-2 bg-white text-sm">Start Date</div>
              <div className="border rounded-md p-2 bg-white text-sm">End Date</div>
            </div>
          </div>
          
          <div className="flex justify-center items-center h-16">
            <span className="text-lg font-semibold text-[#2E364B]">Coming Soon!</span>
          </div>
          
          <div className="text-xs text-gray-500 text-center">
            {type === '30days' ? 'Last 30 Days Selector' : 'Last 7 Days Selector'} - This feature is coming soon
          </div>
        </div>
      </div>
    </>
  );
};

const TooltipWrapper = React.memo(({ content, children, cardName, projectName }) => {
  const [isVisible, setIsVisible] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0, isHeader: false });

  const handleMouseEnter = useCallback((e) => {
    if (!e?.currentTarget) return;
    
    const element = e.currentTarget;
    const rect = element.getBoundingClientRect();
    const isHeaderTooltip = cardName === 'myProjects' || cardName === 'totalHeadcount';
    
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    tooltipTimeoutRef.current = setTimeout(() => {
      setPosition({
        x: rect.right,
        y: rect.top,
        isHeader: isHeaderTooltip
      });
      setIsVisible(true);
      
      // Only track after tooltip is actually shown
      if (cardName) {
        mixpanel.track('Metric info viewed', {
          card: cardName,
          project: projectName || 'All projects'
        });
      }
    }, 300);
  }, [cardName, projectName]);

  const handleMouseLeave = useCallback(() => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    
    tooltipTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 200);
  }, []);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {React.cloneElement(children, {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave
      })}
      {isVisible && (
        <div 
          className="fixed z-[9999] p-4 bg-[#EDF0F5] shadow-[0px_3px_5px_rgba(87,91,98,0.10)] rounded-lg border border-[#B5BBCE]"
          style={{ 
            top: position.y, 
            left: position.x,
            transform: position.isHeader 
              ? 'translate(-100px, 20px)'
              : 'translate(-100px, -100%)',
            minWidth: '280px',
            maxWidth: '320px',
            pointerEvents: 'none'
          }}
        >
          <MarkdownTooltip content={content} />
        </div>
      )}
    </>
  );
});

TooltipWrapper.displayName = 'TooltipWrapper';

// Add custom tooltip formatter component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const dayOfWeek = payload[0]?.payload?.dayOfWeek;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg" style={{ zIndex: 1000000, position: 'relative' }}>
        <p className="text-sm font-semibold mb-2">{label} ({dayOfWeek})</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const PortfolioDashboard = () => {
  const [projectData, setProjectData] = useState({
    totalProjects: 0,
    totalWorkers: 0,
    headcountData: [],
    formSubmissionsData: [],
    projects: [],
    lastUpdated: null
  });

  // Add check for URL parameters FIRST - before any state
  const urlParams = new URLSearchParams(window.location.search);
  const shouldHideHeader = urlParams.has('site') || urlParams.has('headcountToday');
  const userId = urlParams.get('userId');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerType, setDatePickerType] = useState(''); // '30days' or '7days'
  const [showTopStatTooltip, setShowTopStatTooltip] = useState({ projects: false, workers: false });
  const [datePickerPosition, setDatePickerPosition] = useState({ x: 0, y: 0 });
  const [excludeWeekends, setExcludeWeekends] = useState(false);

  const processTimeSeriesData = useCallback((data) => {
    // Sort data by daily average headcount first
    const sortedData = [...data].sort((a, b) => b.headcount.dailyAverage - a.headcount.dailyAverage);
    
    const uniqueDates = [...new Set(
      sortedData.flatMap(site => [
        ...site.dailyHeadcount.map(entry => entry.date),
        ...site.dailySafetyFormSubmissions.map(entry => entry.date)
      ])
    )].sort();

    // Filter out weekends if excludeWeekends is true
    const filteredDates = uniqueDates.filter(date => {
      if (!excludeWeekends) return true;
      const dayOfWeek = new Date(date + 'T00:00:00Z').getUTCDay();
      return dayOfWeek !== 0 && dayOfWeek !== 6; // 0 is Sunday, 6 is Saturday
    });

    const headcountData = filteredDates.map(date => {
      const dateObj = new Date(date + 'T00:00:00Z');
      const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        timeZone: 'UTC'
      });
      const entry = { date: formattedDate, dayOfWeek };
      // Use sortedData to maintain consistent order
      sortedData.forEach(site => {
        const headcount = site.dailyHeadcount.find(d => d.date === date);
        entry[site.displayName] = headcount ? headcount.count : 0;
      });
      return entry;
    });

    const formSubmissionsData = filteredDates.map(date => {
      const dateObj = new Date(date + 'T00:00:00Z');
      const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        timeZone: 'UTC'
      });
      const entry = { date: formattedDate, dayOfWeek };
      // Use sortedData to maintain consistent order
      sortedData.forEach(site => {
        const submissions = site.dailySafetyFormSubmissions.find(d => d.date === date);
        entry[site.displayName] = submissions ? submissions.count : 0;
      });
      return entry;
    });

    return { headcountData, formSubmissionsData };
  }, [excludeWeekends]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Get URL parameters
        const userId = urlParams.get('userId');
        const headcountToday = urlParams.get('headcountToday');
        const site = urlParams.get('site');
        
        let data;
        
        // Try API first, fallback to direct file access
        try {
          const apiUrl = API_URL ? `${API_URL}/api/portfolio-data${userId ? `?userId=${userId}` : ''}` : `/api/portfolio-data${userId ? `?userId=${userId}` : ''}`;
          console.log('Trying API:', apiUrl);
          const response = await fetch(apiUrl);
          if (!response.ok) throw new Error('API not available');
          data = await response.json();
          console.log('Data loaded from API:', data.length, 'sites');
        } catch (apiError) {
          // Fallback to direct file access (for Firebase Hosting)
          console.log('API not available, loading data file directly');
          const response = await fetch('/data/latest.json');
          if (!response.ok) {
            throw new Error('Failed to fetch portfolio data');
          }
          const rawData = await response.json();
          console.log('Raw data loaded:', rawData.length, 'sites');
          
          // Client-side filtering by userId
          if (userId && Array.isArray(rawData)) {
            console.log('Filtering by userId:', userId);
            data = rawData.filter(site => {
              const hasUserIds = site.userIds && Array.isArray(site.userIds);
              const includesUser = hasUserIds && site.userIds.includes(userId);
              console.log('Site:', site.siteId, 'userIds:', site.userIds, 'includes user:', includesUser);
              return includesUser;
            });
            console.log('Filtered data:', data.length, 'sites for user', userId);
          } else {
            data = rawData;
          }
        }

        // Handle empty data
        if (!data || data.length === 0) {
          setProjectData({
            totalProjects: 0,
            totalWorkers: 0,
            headcountData: [],
            formSubmissionsData: [],
            projects: [],
            lastUpdated: null
          });
          setIsLoading(false);
          return;
        }

        // Get the most recent updated_at timestamp
        const lastUpdated = data.length > 0 ? new Date(data[0].updated_at) : null;
        
        // Process time series data
        const { headcountData, formSubmissionsData } = processTimeSeriesData(data);
        
        // Update data with URL parameters if they exist
        const processedData = data.map(siteData => {
          // Remove updated_at from individual site data to avoid confusion
          const { updated_at, ...siteDataWithoutTimestamp } = siteData;
          // If this is the site specified in URL and headcountToday exists, update its headcount
          if (site && headcountToday && siteDataWithoutTimestamp.siteId.toLowerCase() === site.toLowerCase()) {
            return {
              ...siteDataWithoutTimestamp,
              headcount: {
                ...siteDataWithoutTimestamp.headcount,
                today: parseInt(headcountToday, 10)
              }
            };
          }
          return siteDataWithoutTimestamp;
        });
        
        setProjectData({
          totalProjects: processedData.length,
          totalWorkers: processedData.reduce((acc, site) => acc + site.workersCount, 0),
          headcountData,
          formSubmissionsData,
          projects: processedData,
          lastUpdated
        });
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        setError('Failed to load portfolio data. Please try again later.');
        setProjectData({
          totalProjects: 0,
          totalWorkers: 0,
          headcountData: [],
          formSubmissionsData: [],
          projects: [],
          lastUpdated: null
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [processTimeSeriesData]);

  // Add effect to update chart data when weekend filter changes
  useEffect(() => {
    if (projectData.projects.length > 0) {
      const { headcountData, formSubmissionsData } = processTimeSeriesData(projectData.projects);
      setProjectData(prev => ({
        ...prev,
        headcountData,
        formSubmissionsData
      }));
    }
  }, [excludeWeekends, processTimeSeriesData]);

  useEffect(() => {
    // Get userId from URL
    const userId = new URLSearchParams(window.location.search).get('userId');
    
    // Get visible projects from projectData
    const visibleProjects = projectData.projects.map(project => project.displayName);
  
    if (userId) {
      // Identify the user in Mixpanel
      mixpanel.identify(userId);
    }
    
    // Register properties globally with Mixpanel
    const properties = {
      'Visible projects': visibleProjects,
      'Screen name': 'Portfolio View'
    };
    
    // Only include userId in properties if it exists
    if (userId) {
      properties.userId = userId;
    }
    
    mixpanel.register(properties);
  }, [projectData.projects]);

  const tooltipContent = {
    myProjects: "ⓘ My Projects\n\nNumber of active projects in Genda visible to me",
    contractors: "ⓘ Total Companies\n\nTotal number of companies in this project.\n\nSee a full list of contractors under **Site Management → Contractors** or go to **Contractor Analytics** for drilldown.",

    totalHeadcount: "ⓘ Total Headcount\n\nTotal number of distinct workers linked to my projects.\n\nSee headcount trends and contractor breakdown for each project on the **Contractor Analytics** screen.",
    
    headcount: "ⓘ Headcount\n\n**Today:** Total number of workers on site today.\n**Daily Average:** Average workers per day over the past 7 days, excluding weekends.\n\nSee headcount trends and contractor breakdown for each project on the **Contractor Analytics** screen.",
    
    weeklyTurnover: "ⓘ Weekly Turnover\n\nThis metric shows the percentage of workers replaced on the project team over a week. It indicates how many experienced team members have been substituted with new workers who may be less familiar with the project.\n\n• **Didn't show up:** How many workers were present 2 weeks ago but not last week.\n\n• **Showed up for the first time:** How many new workers were added to the project last week.\n\nCalculated from data from last week and 2 weeks ago.",
    
    stackedLevels: "ⓘ Stacked Levels\n\nLevels that may have been overcrowded in the past 7 days.\n\n• **Stacked:** Levels with more than 100 workers or more than 6 trades with a significant overlap duration.\n\n• **Significant Overlap:** Levels with over 90 minutes of trade overlap during a 2-hour period.\n\nShowing a maximum of 3 stacked levels.",
    
    tasksPending: "ⓘ Tasks Not Worked On\n\nGenda compares scheduled tasks (based on the project's lookahead) locations and actual hours worked by the relevant contractors in these locations per day.\n\nThe numbers on the card represent the past 7 day's data.\n\n• **Scheduled:** Total tasks planned.\n\n• **Not Worked On:** Tasks with less than 20 minutes of work on each of its planned days.",
    
    hoistEfficiency: "ⓘ Hoist Efficiency\n\nLast 7 day's average wait time and average daily number of calls.\n\nSee hoist analytics for each project under **Hoists**.",
    
    totalCompanies: "ⓘ Total Companies\n\nTotal number of companies in this project.\n\nSee a full list of contractors under **Site Management → Contractors** or go to **Contractor Analytics** for drilldown."
  };

  const handleCardClick = (cardName, projectName = 'All projects') => {
    mixpanel.track('Metric card clicked', {
      card: cardName,
      project: projectName
    });
  };

  const handleChartHover = (cardName) => {
    mixpanel.track('Chart data viewed', {
      card: cardName
    });
  };

  const handleTimeRangeClick = (context, range) => {
    mixpanel.track('Time range selector clicked', {
      context,
      currentRange: range
    });
  };

  const MetricCard = ({ title, subtitle, value, secondaryValue, info, isDisabled = false, cardName, projectName }) => {
    const [showComingSoon, setShowComingSoon] = useState(false);
    const [showTooltipMobile, setShowTooltipMobile] = useState(false);

    const handleClick = () => {
      if (!isDisabled) {
        handleCardClick(cardName, projectName);
        setShowComingSoon(true);
        setTimeout(() => setShowComingSoon(false), 2000);
      }
    };

    // Helper function to determine if we should show secondary value
    const shouldShowSecondaryValue = () => {
      if (cardName === "stackedLevels") return false;
      return true;
    };

    // Mobile layout (up to md breakpoint)
    const mobileLayout = (
      <div 
        className={`relative p-2 rounded-lg shadow-sm
          ${isDisabled ? 'bg-gray-50' : 'bg-white active:bg-[#EDF0F5]'}
          cursor-pointer border border-transparent hover:border-gray-200 transition-colors duration-200`}
        onClick={handleClick}
      >
        <div className="flex justify-between items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              {info ? (
                <h3 
                  className="text-sm font-semibold text-[#222326] truncate border-b border-dotted border-gray-400 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTooltipMobile(!showTooltipMobile);
                  }}
                >
                  {title}
                </h3>
              ) : (
                <h3 className="text-sm font-semibold text-[#222326] truncate">{title}</h3>
              )}
            </div>
            <p className="text-xs text-[#636467] truncate">{subtitle}</p>
          </div>
          <div className="flex items-baseline shrink-0">
            {isDisabled ? (
              <span className="text-[#636467] text-xs">No Data</span>
            ) : (
              <div className="flex items-baseline gap-1">
                <span className={`${cardName === "stackedLevels" ? 'text-lg font-bold whitespace-normal leading-tight' : 'text-xl font-bold'}`}>{value}</span>
                {shouldShowSecondaryValue() && (
                  <>
                    <span className="text-xl font-bold">/</span>
                    <span className="text-xl font-bold">{secondaryValue}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile tooltip */}
        {showTooltipMobile && (
          <div 
            className="fixed inset-0 z-50"
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltipMobile(false);
            }}
          >
            <div 
              className="absolute left-2 right-2 top-1/2 -translate-y-1/2 bg-[#2E364B] text-white p-3 text-xs rounded-lg shadow-lg mx-auto max-w-sm"
              onClick={e => e.stopPropagation()}
            >
              <MarkdownTooltip content={tooltipContent[cardName]} className="text-white" />
            </div>
          </div>
        )}

        {/* Coming soon tooltip */}
        {showComingSoon && (
          <div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
              bg-[#2E364B]/95 text-white text-sm rounded-lg z-20 px-4 py-2
              flex items-center justify-center animate-fade-in-out text-center min-w-[200px]"
          >
            Drilldown chart coming soon
          </div>
        )}
      </div>
    );

    // Desktop layout (md breakpoint and up)
    const desktopLayout = (
      <div className="relative">
        <div 
          className={`h-[148px] p-4 rounded-lg shadow-[0_3px_5px_rgba(87,91,98,0.10)] 
            flex flex-col justify-between transition-all duration-200 w-full
            ${isDisabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white cursor-pointer hover:bg-[#F7F9FC] active:bg-[#EDF0F5]'}
            ${!isDisabled && 'hover:shadow-[0_5px_8px_rgba(87,91,98,0.15)]'}`}
          onClick={handleClick}
        >
          <div className="flex justify-between items-start mb-4 md:mb-2 [@media(min-width:1200px)]:mb-4">
            <div>
              <h3 className="text-lg md:text-base [@media(min-width:1200px)]:text-lg font-semibold text-[#222326]">{title}</h3>
              <p className="text-sm md:text-xs [@media(min-width:1200px)]:text-sm text-[#222326]">{subtitle}</p>
            </div>
            <div className="w-6 md:w-5 [@media(min-width:1200px)]:w-6" />
          </div>
          <div className="flex items-baseline">
            {isDisabled ? (
              <span className="text-[#636467] text-base md:text-sm [@media(min-width:1200px)]:text-base">No Data Available</span>
            ) : (
              <div className="flex items-baseline">
                <span className={`${cardName === "stackedLevels" ? 'text-2xl md:text-xl [@media(min-width:1200px)]:text-2xl leading-tight font-bold whitespace-normal' : 'text-3xl md:text-2xl [@media(min-width:1200px)]:text-3xl leading-none font-bold'}`}>{value}</span>
                {shouldShowSecondaryValue() && (
                  <>
                    <span className="mx-2 text-3xl md:text-2xl [@media(min-width:1200px)]:text-3xl font-bold">/</span>
                    <span className="text-3xl md:text-2xl [@media(min-width:1200px)]:text-3xl font-bold">{secondaryValue}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info icon as an overlay */}
        {info && (
          <div className="absolute top-4 right-4 z-10">
            <TooltipWrapper
              content={tooltipContent[cardName]}
              cardName={cardName}
              projectName={projectName}
            >
              <Info 
                size={22}
                className="w-6 md:w-5 [@media(min-width:1200px)]:w-6 text-gray-400 cursor-pointer
                  hover:text-gray-600 transition-colors duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.innerWidth < 768) {
                    setShowTooltipMobile(!showTooltipMobile);
                  }
                }}
              />
            </TooltipWrapper>
          </div>
        )}

        {/* Coming soon tooltip - Updated positioning */}
        {showComingSoon && (
          <div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
              bg-[#2E364B]/95 text-white text-sm rounded-lg z-20 px-4 py-2
              flex items-center justify-center animate-fade-in-out text-center min-w-[200px]"
          >
            Drilldown chart coming soon
          </div>
        )}
      </div>
    );

    return (
      <>
        <div className="block [@media(min-width:768px)]:hidden">
          {mobileLayout}
        </div>
        <div className="hidden [@media(min-width:768px)]:block">
          {desktopLayout}
        </div>
      </>
    );
  };

  const ProjectCard = ({ project }) => (
    <div className="p-4 [@media(min-width:1200px)]:p-6 bg-[#EDF0F5] rounded-lg shadow-lg mb-8">
      <div className="flex flex-col [@media(min-width:1200px)]:flex-row gap-4 [@media(min-width:1200px)]:gap-6">
        <div className="h-40 [@media(min-width:1200px)]:h-[200px] [@media(min-width:1200px)]:w-32 w-full overflow-hidden">
          {project.portfolioCardImageUrl ? (
            <img 
              src={project.portfolioCardImageUrl} 
              alt={project.displayName}
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
              <img 
                src="./assets/images/Buildings.svg" 
                alt="Project placeholder"
                className="w-12 h-12 opacity-50"
              />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col [@media(min-width:1200px)]:flex-row [@media(min-width:1200px)]:items-center gap-3 [@media(min-width:1200px)]:gap-0 [@media(min-width:1200px)]:justify-between mb-4 [@media(min-width:1200px)]:mb-2">
            <div className="flex items-center gap-2">
              <img 
                src="./assets/images/Buildings.svg" 
                alt="Buildings icon" 
                className="w-6 h-6"
              />
              <h2 className="text-xl [@media(min-width:1200px)]:text-2xl font-semibold">{project.displayName}</h2>
            </div>
            <div className="flex flex-wrap [@media(min-width:1200px)]:flex-nowrap items-center gap-3 [@media(min-width:1200px)]:gap-4">
              <span 
                className="px-4 py-1 text-sm underline decoration-dotted decoration-[#848BA5] text-[#606983] font-semibold"
                data-card-name="contractors"
                data-project-name={project.displayName}
              >
                {project.contractorsCount} Contractors
              </span>
              <span className="text-[#606983]">{project.addressDetails.city}, {project.addressDetails.code}</span>
              <div className="flex items-center bg-white rounded-full px-4 py-1">
                <span className="font-semibold mr-2">{project.weather.temperature}°F</span>
                <img 
                  src={project.weather.iconUrl} 
                  alt={project.weather.condition}
                  className="w-8 h-8"
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 [@media(min-width:1199px)]:grid-cols-5 gap-4 mt-3 relative z-10">
            <MetricCard
              title="Headcount"
              subtitle="Today / Daily Average"
              value={project.headcount.today}
              secondaryValue={project.headcount.dailyAverage}
              info
              cardName="headcount"
              projectName={project.displayName}
            />
            <MetricCard
              title="Weekly Turnover"
              subtitle="No-Shows / First Day Workers"
              value={project.weeklyTurnover.didNotShowUp}
              secondaryValue={project.weeklyTurnover.newWorkers}
              info
              cardName="weeklyTurnover"
              projectName={project.displayName}
            />
            <MetricCard
              title="Stacked Levels"
              subtitle="Most congested levels"
              value={project.stackedLevels.join(', ')}
              isDisabled={!project.stackedLevels.length}
              info
              cardName="stackedLevels"
              projectName={project.displayName}
            />
            <MetricCard
              title="Tasks Pending"
              subtitle="Not Worked On / Scheduled"
              value={project.tasksPending.notWorkedOn}
              secondaryValue={project.tasksPending.scheduledTasks}
              isDisabled={!project.tasksPending.scheduledTasks}
              info
              cardName="tasksPending"
              projectName={project.displayName}
            />
            <MetricCard
              title="Hoist Efficiency"
              subtitle="Average Wait Minutes / Average Calls"
              value={project.hoistEfficiency.averageWaitTime}
              secondaryValue={project.hoistEfficiency.numberOfCalls}
              isDisabled={!project.hoistEfficiency.numberOfCalls}
              info
              cardName="hoistEfficiency"
              projectName={project.displayName}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Calculate total submissions per project for sorting
  const projectTotalSubmissions = {};
  projectData.projects.forEach(project => {
    projectTotalSubmissions[project.displayName] = projectData.formSubmissionsData
      .reduce((sum, dataPoint) => sum + (dataPoint[project.displayName] || 0), 0);
  });

  // Sort projects by total submissions
  const sortedProjectsBySubmissions = [...projectData.projects]
    .sort((a, b) => 
      (projectTotalSubmissions[b.displayName] || 0) - (projectTotalSubmissions[a.displayName] || 0)
    );

  // Generate chart colors based on headcount for consistency
  const chartColors = projectData.projects.length > 0 
    ? [...projectData.projects]
        .sort((a, b) => b.headcount.dailyAverage - a.headcount.dailyAverage)
        .reduce((acc, project, index) => {
          const colors = [
            '#00796B', // Dark Teal
            '#B09CFF', // Purple
            '#FF7557', // Coral
            '#4CAF50', // Green
            '#2196F3', // Blue
            '#FFC107', // Amber
            '#9C27B0', // Deep Purple
            '#FF5722', // Deep Orange
            '#00BCD4', // Cyan
            '#3F51B5', // Indigo
            '#E91E63', // Pink
            '#009688', // Teal
          ];
          acc[project.displayName] = colors[index % colors.length];
          return acc;
        }, {})
    : {};

  // New color mapping for form submissions based on submission counts
  const formSubmissionColors = projectData.projects.length > 0 
    ? [...projectData.projects]
        .map(project => ({
          name: project.displayName,
          avgSubmissions: projectData.formSubmissionsData.reduce((sum, day) => sum + (day[project.displayName] || 0), 0) / projectData.formSubmissionsData.length
        }))
        .sort((a, b) => b.avgSubmissions - a.avgSubmissions)
        .reduce((acc, project, index) => {
          const colors = [
            '#00796B', // Dark Teal (replacing light turquoise)
            '#B09CFF', // Purple
            '#FF7557', // Coral
            '#4CAF50', // Green
            '#2196F3', // Blue
            '#FFC107', // Amber
            '#9C27B0', // Deep Purple
            '#FF5722', // Deep Orange
            '#00BCD4', // Cyan
            '#3F51B5', // Indigo
            '#E91E63', // Pink
            '#009688', // Teal
          ];
          acc[project.name] = colors[index % colors.length];
          return acc;
        }, {})
    : {};

  // Require userId - show immediately without waiting for data
  if (!userId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Portfolio Dashboard</h1>
            <p className="text-gray-600">Access Required</p>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800">
              This dashboard requires authentication. Please use your personalized access link.
            </p>
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>Don't have access?</p>
            <p className="mt-1">Contact your administrator for your personalized dashboard link.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Dashboard</h2>
            <p className="text-gray-600 text-sm">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no projects for this user
  if (projectData.projects.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">📊</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Projects Available</h2>
            <p className="text-gray-600">
              You don't have access to any projects yet.
            </p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              If you believe this is an error, please contact your administrator to grant you access to projects.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Top Navigation */}
      {!shouldHideHeader && (
        <div className="bg-[#2E364B] h-[48px] px-8 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-[26px]">
              <div className="w-12 h-12 flex items-center justify-center">
                <img src="./assets/images/menu_24px.svg" alt="Logo" className="rounded-full" />
              </div>
              <div className="w-[91px] h-6">
                <img src="./assets/images/GendaLogoFull.svg" alt="Genda" className="h-full" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <button className="w-12 h-12 rounded-full bg-[#293146] flex items-center justify-center">
              <img src="./assets/images/Bell.svg" alt="Settings" />
            </button>
            <div className="hidden [@media(min-width:768px)]:flex px-4 py-1 border border-[#606983] rounded-full items-center gap-2">
              <span className="text-[#F7F9FC] text-lg">EMERGENCY PA</span>
            </div>
            <button className="w-12 h-12 rounded-full bg-[#293146] flex items-center justify-center">
              <img src="./assets/images/circle1.svg" alt="Profile" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-[#F7F9FC] relative" style={{background: 'linear-gradient(to bottom, #606983 266px, #F7F9FC 266px)'}}>
        <div className="max-w-[1680px] mx-auto px-4 [@media(min-width:1199px)]:px-16 py-4">
          {/* Stats Row */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <div 
                  className="relative bg-[#2E364B] text-white px-6 py-2 rounded-[27px] shadow-[0_3px_5px_0_rgba(87,91,98,0.1)] 
                    text-lg flex items-center gap-2 cursor-pointer hover:bg-[#3a4359] transition-colors duration-200"
                  onClick={() => {
                    setShowTopStatTooltip(prev => ({ ...prev, projects: true }));
                    setTimeout(() => setShowTopStatTooltip(prev => ({ ...prev, projects: false })), 2000);
                  }}
                >
                  <span>{projectData.totalProjects} Projects</span>
                  <TooltipWrapper
                    content={tooltipContent.myProjects}
                    cardName="myProjects"
                  >
                    <Info 
                      size={20}
                      className="text-gray-400"
                    />
                  </TooltipWrapper>
                  {showTopStatTooltip.projects && (
                    <div className="absolute top-[calc(100%+8px)] left-1/2 transform -translate-x-1/2
                      bg-[#2E364B] text-white px-4 py-2 rounded-lg shadow-lg z-10
                      animate-fade-in-out whitespace-nowrap text-center"
                    >
                      Coming Soon
                    </div>
                  )}
                </div>
                <div 
                  className="relative bg-[#2E364B] text-white px-6 py-2 rounded-[27px] shadow-[0_3px_5px_0_rgba(87,91,98,0.1)] 
                    text-lg flex items-center gap-2 cursor-pointer hover:bg-[#3a4359] transition-colors duration-200"
                  onClick={() => {
                    setShowTopStatTooltip(prev => ({ ...prev, workers: true }));
                    setTimeout(() => setShowTopStatTooltip(prev => ({ ...prev, workers: false })), 2000);
                  }}
                >
                  <span>{projectData.totalWorkers} Workers</span>
                  <TooltipWrapper
                    content={tooltipContent.totalHeadcount}
                    cardName="totalHeadcount"
                  >
                    <Info 
                      size={20}
                      className="text-gray-400"
                    />
                  </TooltipWrapper>
                  {showTopStatTooltip.workers && (
                    <div className="absolute top-[calc(100%+8px)] left-1/2 transform -translate-x-1/2
                      bg-[#2E364B] text-white px-4 py-2 rounded-lg shadow-lg z-10
                      animate-fade-in-out whitespace-nowrap">
                      Coming Soon
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-400 flex items-center whitespace-nowrap">
                  Updated at: <span className="ml-1 text-gray-300">
                    {projectData.lastUpdated ? projectData.lastUpdated.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit', 
                      hour12: true 
                    }) : '--:--'}
                  </span>
                </div>
              </div>
            </div>
            <div 
              className="bg-[#FE8002] text-white px-4 py-0.5 rounded-full text-lg cursor-pointer
                hover:bg-[#ff9124] transition-colors duration-200 self-end"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setDatePickerPosition({
                  x: rect.left,
                  y: rect.bottom + 8
                });
                setDatePickerType('30days');
                setShowDatePicker(true);
              }}
            >
              Last 30 Days
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 [@media(min-width:1200px)]:grid-cols-2 gap-6 mb-8">
            <Card className={`shadow-[0_3px_5px_0_rgba(87,91,98,0.1)] relative ${projectData.projects.length > 15 ? 'h-[500px] [@media(min-width:768px)]:h-[500px]' : 'h-[294px] [@media(min-width:768px)]:h-[294px] h-[350px]'}`}>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle className="text-base font-semibold">Daily Headcount - 30 Days</CardTitle>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={excludeWeekends}
                    onChange={(e) => {
                      setExcludeWeekends(e.target.checked);
                      mixpanel.track('Exclude weekends toggled', {
                        value: e.target.checked,
                        context: 'Portfolio View'
                      });
                    }}
                    label="Exclude Weekends"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className={`mt-[-10px] ${projectData.projects.length > 15 ? 'h-[454px] [@media(min-width:768px)]:h-[454px]' : 'h-[248px] [@media(min-width:768px)]:h-[248px] h-[304px]'}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={projectData.headcountData}
                      margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
                      height={Math.max(200, 150 + (projectData.projects.length * 25))}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDF0F5" />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#606983', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#606983', fontSize: 12 }}
                      />
                      <Tooltip 
                        content={<CustomTooltip />} 
                        wrapperStyle={{ zIndex: 1000000 }}
                        cursor={{ stroke: '#606983', strokeWidth: 1 }}
                      />
                      <Legend 
                        align="left"
                        verticalAlign="top"
                        layout="horizontal"
                        wrapperStyle={{ 
                          top: -10,
                          left: 65,
                          width: '80%',
                          marginBottom: 0
                        }}
                        iconType="circle"
                        iconSize={7}
                      />
                      {Object.entries(chartColors).map(([name, color]) => (
                        <Line
                          key={name}
                          type="monotone"
                          dataKey={name}
                          name={name}
                          stroke={color}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className={`shadow-[0_3px_5px_0_rgba(87,91,98,0.1)] relative ${projectData.projects.length > 15 ? 'h-[500px] [@media(min-width:768px)]:h-[500px]' : 'h-[294px] [@media(min-width:768px)]:h-[294px] h-[350px]'}`}>
              <CardHeader className="flex flex-row justify-between items-center">
                <CardTitle className="text-base font-semibold">Form Submissions - 30 Days</CardTitle>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={excludeWeekends}
                    onChange={(e) => {
                      setExcludeWeekends(e.target.checked);
                      mixpanel.track('Exclude weekends toggled', {
                        value: e.target.checked,
                        context: 'Portfolio View'
                      });
                    }}
                    label="Exclude Weekends"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className={`mt-[-10px] ${projectData.projects.length > 15 ? 'h-[454px] [@media(min-width:768px)]:h-[454px]' : 'h-[248px] [@media(min-width:768px)]:h-[248px] h-[304px]'}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={projectData.formSubmissionsData}
                      margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
                      height={Math.max(200, 150 + (projectData.projects.length * 25))}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDF0F5" />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#606983', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#606983', fontSize: 12 }}
                      />
                      <Tooltip 
                        content={<CustomTooltip />} 
                        wrapperStyle={{ zIndex: 1000000 }}
                        cursor={{ stroke: '#606983', strokeWidth: 1 }}
                      />
                      <Legend 
                        align="left"
                        verticalAlign="top"
                        layout="horizontal"
                        wrapperStyle={{ 
                          top: -10,
                          left: 65,
                          width: '80%',
                          marginBottom: 0
                        }}
                        iconType="circle"
                        iconSize={7}
                      />
                      {sortedProjectsBySubmissions.map((project) => (
                        <Line
                          key={project.displayName}
                          type="monotone"
                          dataKey={project.displayName}
                          name={project.displayName}
                          stroke={chartColors[project.displayName]}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search Input */}
          <div className="max-w-full [@media(min-width:1200px)]:max-w-[480px] mx-auto mb-6 px-4 [@media(min-width:1200px)]:px-0">
            <DataRequestInput 
              userId={new URLSearchParams(window.location.search).get('userId')} 
              visibleProjects={projectData.projects.map(project => project.displayName)}
            />
          </div>

          {/* Projects Section */}
          <div className="mb-9 mx-auto w-full [@media(min-width:1199px)]:max-w-[1200px]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">My Projects</h2>
              <div 
                className="bg-[#FE8002] text-white px-4 py-0.5 rounded-full text-lg cursor-pointer
                  hover:bg-[#ff9124] transition-colors duration-200"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setDatePickerPosition({
                    x: rect.left,
                    y: rect.bottom + 8
                  });
                  setDatePickerType('7days');
                  setShowDatePicker(true);
                }}
              >
                Last 7 Days
              </div>
            </div>

            {/* Project Cards */}
            <div className="space-y-8 w-full">
              {projectData.projects
                .sort((a, b) => b.headcount.dailyAverage - a.headcount.dailyAverage)
                .map(project => (
                  <ProjectCard key={project.siteId} project={project} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <DisabledDatePicker 
          onClose={() => setShowDatePicker(false)}
          type={datePickerType}
          position={datePickerPosition}
        />
      )}
    </div>
  );
};